import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';

/**
 * Het hoofdmenu, gedeeld door de menubalk (Schil.jsx) en de groepspagina's (pages/Groep.jsx), zoals in
 * ADTS-ONE: elke groep is een eigen pagina met zijn onderliggende items als tegels. De teksten staan als
 * `titel` en `label`, zodat scripts/controleer-vertalingen.js ze als gebruikt ziet (en op Engels nakijkt).
 *
 * Een item is zichtbaar als je zijn recht hebt (of het recht van de groep); een groep verschijnt zodra er
 * een item van over is. "Alle objecten" en de menubalk tonen dus precies hetzelfde.
 */
export const GROEPEN = [
  { id: 'monitoring', titel: 'Monitoring', recht: 'alerts.lezen', items: [
    { pad: '/kaart', label: 'Aanvallen', icoon: 'wereld' },
    { pad: '/alerts', label: 'Alerts', icoon: 'bel', teller: 'alerts' },
    { pad: '/blokkades', label: 'Blokkades', icoon: 'blokkeren', teller: 'blokkades' },
  ] },
  { id: 'compliance', titel: 'Compliance', recht: 'alerts.lezen', items: [
    { pad: '/status', label: 'Beveiligingsstatus', icoon: 'schild' },
    { pad: '/volgende-stappen', label: 'Volgende stappen', icoon: 'ster' },
  ] },
  { id: 'cloud', titel: 'Cloud', recht: 'alerts.lezen', items: [
    { pad: '/umbrella', label: 'DNS Security', icoon: 'umbrella' },
    { pad: '/mail', label: 'Email Security', icoon: 'mail' },
    { pad: '/duo', label: 'Secure Connect', icoon: 'duo' },
    { pad: '/endpoints', label: 'Secure Endpoint', icoon: 'secureendpoint' },
    { pad: '/vpn', label: 'VPN', icoon: 'vpn' },
  ] },
  { id: 'infra', titel: 'Infra', recht: 'alerts.lezen', items: [
    { pad: '/firewall', label: 'Firewall', icoon: 'firewall' },
    { pad: '/identity', label: 'Identity', icoon: 'identity' },
    { pad: '/scans', label: 'Scans', icoon: 'scan' },
  ] },
  { id: 'beheer', titel: 'Beheer', items: [
    { pad: '/bronnen', label: 'Bronnen', icoon: 'basis', recht: 'alerts.lezen' },
    { pad: '/logboek', label: 'Logboek', icoon: 'boek', recht: 'logboek.lezen' },
    { pad: '/modules', label: 'Modules', icoon: 'raster', recht: 'alerts.lezen' },
  ] },
];

/** De groepen en items die je mag zien. */
export const groepenVoor = (mag) => GROEPEN
  .map((g) => ({ ...g, items: g.items.filter((i) => mag(i.recht ?? g.recht)) }))
  .filter((g) => (g.recht ? mag(g.recht) : true) && g.items.length > 0);

/** Het pad van de groep waar een pagina onder valt (voor het oplichten van het menu). */
export const groepVanPad = (mag, pad) => groepenVoor(mag)
  .find((g) => g.items.some((i) => pad === i.pad || pad.startsWith(`${i.pad}/`)));

/** De tellers bij de menu-items: nieuwe alerts en wachtende blokkades. Dezelfde vragen als de schermen zelf,
 *  dus geen extra werk (React Query deelt de uitkomst). */
export function useMenuTellers(mag, gekozen) {
  const magLezen = mag('alerts.lezen');
  const alerts = useQuery({ queryKey: ['alerts', gekozen, 'open'], queryFn: () => api.alerts(gekozen), refetchInterval: 15_000, enabled: magLezen });
  const blok = useQuery({ queryKey: ['blokkades', 'wachten', gekozen], queryFn: () => api.blokkadesWachten(gekozen), refetchInterval: 30_000, enabled: magLezen });
  return { alerts: alerts.data?.data.counts.nieuw ?? 0, blokkades: blok.data?.data.n ?? 0 };
}
